import {Component} from 'react';
import PropTypes from 'prop-types';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import {I18n, Translate} from 'react-redux-i18n';

import {Icon, stringUtils} from 'front-onceforall-core';
import {DOSSIER_TYPE} from 'front-onceforall-core/dist/utils/documents';
import {isEmpty} from 'front-onceforall-core/dist/utils/arrays';
import {COUNTRY} from 'front-onceforall-core/dist/utils/country';

import authenticationService from '../../services/authentication/AuthenticationService';
import documentTypeService from '../../services/documentType/DocumentTypeService';
import {addError, addSuccess} from '../../actions/snackbars';
import {getAdditionalDocumentTypes, getBusinessDocumentTypes, getLegalDocumentTypes, getProjectDocumentTypes} from '../../utils/documentType';
import {MANAGE_PROJECT_PERMISSION, PROJECT_STATUS} from '../../utils/constant';
import {captureError} from '../../utils/log';
import {deepCopy} from '../../utils/objects';

import {getBusinessLineTooltip} from './utils';
import Checkbox from './Checkbox';

export class ProjectRequiredDocumentsForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      submitted: false,
      documentTypes: [],
      requiredDocumentTypes: []
    };

    this.isBackOffice = authenticationService.roles().isBackOffice();
    this.canManageProject = authenticationService.roles().hasPermission(MANAGE_PROJECT_PERMISSION);
    this.isProjectOwner = props.project.ownerOrganisation.organisationId === props.organisation.organisationId;
    this.isReadOnly = !this.canManageProject || !this.isProjectOwner;
    this.displayInfoMessage = [COUNTRY.FRANCE, COUNTRY.GERMANY, COUNTRY.ITALY].includes(this.props.project?.country);
    this.displayInfoTooltip = [COUNTRY.FRANCE, COUNTRY.GERMANY].includes(this.props.project?.country);
  }

  componentDidMount() {
    if (this.props.project?.projectStatus !== PROJECT_STATUS.CLOSED) {
      Promise.all([
        documentTypeService.getByCountryAndProjectType(this.props.project.country, this.props.project.projectType),
        documentTypeService.getRequiredDocumentTypes(this.props.project.projectId)
      ])
        .then(([documentTypes, requiredDocumentTypes]) => {
          this.setState({
            loading: false,
            documentTypes,
            requiredDocumentTypes: requiredDocumentTypes.map(obligation => ({requiredInProject: true, ...obligation}))
          });
        })
        .catch(error => {
          captureError('Error while fetching document types for project', error);
          this.props.actions.addError(<Translate value="error.generic"/>);
          this.setState({loading: false});
        });
    }
  }

  handleSubmit = event => {
    event.preventDefault();

    if (this.isReadOnly || this.state.submitted) {
      return;
    }

    this.setState({submitted: true});

    const requiredDocumentTypes = this.state.requiredDocumentTypes
      .flatMap(obligation => (
        !obligation.requiredInProject ?
          [] :
          ({
            documentTypeId: obligation.documentTypeId,
            requiredInDossierOverviewStatus: obligation.requiredInDossierOverviewStatus
          })
      ));

    documentTypeService.setRequiredDocumentTypes(this.props.project.projectId, requiredDocumentTypes)
      .then(() => {
        this.props.actions.addSuccess(<Translate value="message.changesSaved"/>);
        if (this.props.isProjectCreationFunnel) {
          this.props.goToNextStep();
        } else {
          this.props.onClose();
        }
      })
      .catch(() => {
        this.props.actions.addError(<Translate value="error.generic"/>);
      })
      .finally(() => {
        this.setState({submitted: false});
      });
  };

  handleCheckboxChange = (key, subtypeId) => {
    const reqDocTypes = deepCopy(this.state.requiredDocumentTypes);
    const idx = reqDocTypes.findIndex(documentType => documentType.documentTypeId === subtypeId);

    if (idx === -1) {
      reqDocTypes.push({
        documentTypeId: subtypeId,
        requiredInProject: true,
        requiredInDossierOverviewStatus: key === 'requiredInDossierOverviewStatus'
      });
    } else {
      const docType = deepCopy(reqDocTypes[idx]);
      docType[key] = !docType[key];
      if (!docType.requiredInProject) {
        docType.requiredInDossierOverviewStatus = false;
      }
      reqDocTypes[idx] = docType;
    }

    this.setState({
      requiredDocumentTypes: deepCopy(reqDocTypes)
    });
  };

  buildRequiredCheckbox = subtype => {
    const requiredType = this.state.requiredDocumentTypes
      .find(type => type.documentTypeId === subtype.documentTypeId);

    const isLegalType = subtype.dossier === DOSSIER_TYPE.LEGAL;
    const notEditable = !this.isBackOffice && subtype.obligationConfiguration === 'CHECKABLE_BO';
    const onlyBO = this.isBackOffice && !this.isReadOnly && subtype.obligationConfiguration === 'CHECKABLE_BO';
    const alwaysChecked = subtype.obligationConfiguration === 'ALWAYS_CHECKED';
    const requiredInProject = !!requiredType?.requiredInProject;
    const requiredInDossierOverviewStatus = !!requiredType?.requiredInDossierOverviewStatus;
    const disabledCheckboxDossierOverview = isLegalType || !requiredInProject;

    const projectCheckbox = (
      <Checkbox
        key={`projectCheckbox-${subtype.documentTypeId}`}
        data-id={`project-${subtype.documentTypeId}`}
        className={(!alwaysChecked && onlyBO) ? ' background-bo' : ''}
        documentTypeId={subtype.documentTypeId}
        checked={alwaysChecked || requiredInProject}
        disabled={alwaysChecked || this.isReadOnly || notEditable}
        onChange={() => this.handleCheckboxChange('requiredInProject', subtype.documentTypeId)}
        label={'project.edit.requiredForProject'}
        notEditable={notEditable}
      />
    );

    const dossierOverviewCheckbox = (
      <Checkbox
        key={`dossierOverviewCheckbox-${subtype.documentTypeId}`}
        data-id={`dossierOverview-${subtype.documentTypeId}`}
        className={!disabledCheckboxDossierOverview && onlyBO ? ' background-bo' : ''}
        documentTypeId={subtype.documentTypeId}
        checked={requiredInDossierOverviewStatus}
        disabled={disabledCheckboxDossierOverview || this.isReadOnly || notEditable}
        onChange={() => this.handleCheckboxChange('requiredInDossierOverviewStatus', subtype.documentTypeId)}
        label={'project.edit.requiredForDossierOverviewStatus'}
        notEditable={notEditable}
      />
    );

    return (
      <>
        {projectCheckbox}
        {dossierOverviewCheckbox}
      </>
    );
  };

  buildRow = (type, index) => {
    const tooltip = this.displayInfoTooltip ?
      getBusinessLineTooltip(type) : '';
    return (
      <div
        key={`type-${index}`}
        data-dossier={type.dossier}
        className="row align-items-center background-on-hover"
      >
        <span className="ellipsis col-md-6">
          {type.name}
          {tooltip && <Icon
            tooltip={tooltip}
            icon="info"
            className="ml-2"
          />}
        </span>
        {this.buildRequiredCheckbox(type)}
      </div>
    );
  };

  buildDocumentTypes = documentTypes => {
    const rows = documentTypes.map(this.buildRow);

    return (
      <>
        <div className="row d-none d-md-flex">
          <span className="offset-md-6 col-md-3"><Translate value="project.edit.requiredForProject"/></span>
          <span className="col-md-3"><Translate value="project.edit.requiredForDossierOverviewStatus"/></span>
        </div>
        {rows}
      </>
    );
  };

  renderCheckboxesByType = (documentTypes, label) => {
    if (!documentTypes || isEmpty(documentTypes)) {
      return null;
    }
    return (
      <div className="box mt-4">
        <h3><Translate value={label}/></h3>
        {this.buildDocumentTypes(documentTypes)}
      </div>
    );
  };

  renderInfoMessages = () => (
    <div className="box info">
      <div>
        {stringUtils.getStrongs(I18n.t('project.requiredDocuments.message1'))}
      </div>
      <div className="mt-3">
        {stringUtils.getStrongs(I18n.t('project.requiredDocuments.message2'))}
      </div>
    </div>
  );

  render() {
    const {documentTypes} = this.state;
    const legalDocumentTypes = getLegalDocumentTypes(documentTypes);
    const businessDocumentTypes = getBusinessDocumentTypes(documentTypes);
    const projectDocumentTypes = getProjectDocumentTypes(documentTypes);
    const additionalDocumentTypes = getAdditionalDocumentTypes(documentTypes);

    if (this.state.loading) {
      return <div className="table-without-result loading-overlay"></div>;
    }

    return (
      <div>
        <form onSubmit={this.handleSubmit}>
          {!this.props.isProjectCreationFunnel && (
            <div className="font-size-xl mb-4">
              <strong><Translate value="project.requiredDocuments.title"/></strong>
            </div>
          )}
          {this.displayInfoMessage && this.renderInfoMessages()}

          <div
            className="mt-5"
            style={{position: 'relative'}}
          >
            {this.renderCheckboxesByType(legalDocumentTypes, 'dossier.labels.LEGAL')}
            {this.renderCheckboxesByType(businessDocumentTypes, 'dossier.labels.BUSINESS.ALL')}
            {this.renderCheckboxesByType(projectDocumentTypes, 'dossier.labels.PROJECT')}
            {this.renderCheckboxesByType(additionalDocumentTypes, 'dossier.labels.ADDITIONAL')}

            <div
              className="d-flex justify-content-end mt-5"
              style={{marginBottom: '70px'}}
            >
              {this.props.isProjectCreationFunnel &&
                <button
                  className="large flex-container mr-5"
                  type="button"
                  disabled={this.state.submitted}
                  onClick={this.props.skipStep}
                >
                  <Translate value="action.skip"/>
                </button>
              }
              {!this.isReadOnly &&
                <button
                  className="large primary flex-container"
                  type="submit"
                  disabled={this.state.submitted}
                >
                  <Icon icon={this.state.submitted ? 'loader' : 'check'}/>
                  <Translate value="project.edit.editDocuments"/>
                </button>
              }
            </div>
          </div>
        </form>
      </div>
    );
  }
}

ProjectRequiredDocumentsForm.defaultProps = {
  goToNextStep: () => {},
  skipStep: () => {},
  isProjectCreationFunnel: false
};

ProjectRequiredDocumentsForm.propTypes = {
  project: PropTypes.object.isRequired,
  organisation: PropTypes.object.isRequired,
  goToNextStep: PropTypes.func,
  skipStep: PropTypes.func,
  isProjectCreationFunnel: PropTypes.bool,
  actions: PropTypes.shape({
    addError: PropTypes.func.isRequired,
    addSuccess: PropTypes.func.isRequired
  }).isRequired,
  onClose: PropTypes.func
};

function mapStateToProps(state) {
  return {
    organisation: state.organisation
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({addError, addSuccess}, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectRequiredDocumentsForm);
